import { LogLevels, isCritical } from '../logLevel.js';
import { Transport, LogMessage, HttpTransportOptions } from '../types.js';
import { logRocket } from './logrocket.js';

const DEFAULT_FLUSH_INTERVAL = 10_000;
const DEFAULT_MAX_BATCH_SIZE = 50;

/** Typical browser cap for `sendBeacon` / keepalive POST bodies (UTF-8 bytes). */
const MAX_SENDBEACON_UTF8_BYTES = 64 * 1024;

const utf8Encoder = new TextEncoder();

/**
 * UTF-8 byte length using `TextEncoder` (supported in target browsers; polyfilled in Jest via `lib/tests/jest-env.ts`).
 * @param s
 */
const utf8ByteLength = (s: string): number => utf8Encoder.encode(s).length;

/**
 * get http transport handler
 * @param url diagnostics endpoint url. e.g. https://api.ellielabs.com/diagnostics/v2/logging
 * @param options {@link HttpTransportOptions} optional configuration for batching, rate limiting, and sampling
 * @returns http transport handler
 */
export const http = (
  url: string,
  options?: HttpTransportOptions,
): Transport => {
  const {
    flushInterval = DEFAULT_FLUSH_INTERVAL,
    maxBatchSize = DEFAULT_MAX_BATCH_SIZE,
    maxLogsPerInterval = 0,
    samplingRate = 1,
  } = options ?? {};

  let logs: LogMessage[] = [];
  let initialized = false;
  let logCountInWindow = 0;

  const postLogsBody = (body: string) =>
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
      },
      keepalive: true,
      body,
    });

  const sendViaFetch = (body: string) =>
    postLogsBody(body)
      .then(async (res) => {
        if (res.ok) return res.text();
        throw new Error(`status: ${res.status}, ${res.statusText}`);
      })
      .catch((err: unknown) => {
        console.warn('pui-diagnostics: failed to send logs', err); // eslint-disable-line no-console
      });

  /**
   * `true` if the request completed with a 2xx response; `false` on non-OK or network error (no logging).
   * @param body
   */
  const sendViaFetchSucceeded = (body: string): Promise<boolean> =>
    postLogsBody(body)
      .then(async (res) => {
        if (!res.ok) return false;
        await res.text();
        return true;
      })
      .catch(() => false);

  // Fire-and-forget: `sendViaFetch` logs failures with `console.warn`; callers do not await.
  const sendViaFetchDetached = (body: string): void => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- rejection handled inside sendViaFetch
    sendViaFetch(body);
  };

  const sendLogs = async (logMsg: LogMessage | LogMessage[]) => {
    const beaconAvailable =
      typeof window !== 'undefined' &&
      typeof window.navigator?.sendBeacon === 'function';

    const sendChunkViaBeaconOrFetch = (chunk: LogMessage[]): void => {
      if (chunk.length === 0) return;
      const body = JSON.stringify(chunk);
      if (beaconAvailable) {
        try {
          if (navigator.sendBeacon(url, body)) return;
        } catch {
          sendViaFetchDetached(body);
          return;
        }
        // sendBeacon returned false — try fetch first; only split when fetch fails and batch is likely oversized
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- continuation splits or warns after fetch settles
        sendViaFetchSucceeded(body).then((sent) => {
          if (sent) return;
          const bodyUtf8Bytes = utf8ByteLength(body);
          if (chunk.length > 1 && bodyUtf8Bytes > MAX_SENDBEACON_UTF8_BYTES) {
            const mid = Math.ceil(chunk.length / 2);
            sendChunkViaBeaconOrFetch(chunk.slice(0, mid));
            sendChunkViaBeaconOrFetch(chunk.slice(mid));
            return;
          }
          // eslint-disable-next-line no-console -- beacon + fetch both failed; splitting would not help
          console.warn(
            `pui-diagnostics: could not send logs after sendBeacon returned false and fetch failed (${bodyUtf8Bytes} UTF-8 bytes, ${chunk.length} log row(s)).`,
          );
        });
        return;
      }
      sendViaFetchDetached(body);
    };

    if (Array.isArray(logMsg)) {
      sendChunkViaBeaconOrFetch(logMsg);
      return true;
    }

    const body = JSON.stringify(logMsg);
    if (beaconAvailable) {
      try {
        if (navigator.sendBeacon(url, body)) return true;
      } catch {
        // sendBeacon threw — fall through to fetch
      }
    }
    return sendViaFetch(body);
  };

  const flushBatch = () => {
    if (logs.length === 0) return;
    const batch = logs;
    logs = [];
    sendLogs(batch).catch(console.error); // eslint-disable-line no-console
  };

  const init = () => {
    if (initialized) return;
    initialized = true;
    window.addEventListener('beforeunload', flushBatch);
    window.addEventListener('pagehide', flushBatch);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushBatch();
    });
    setInterval(() => {
      flushBatch();
      logCountInWindow = 0;
    }, flushInterval);
  };

  return {
    log: async (msg: LogMessage, logLevel?: LogLevels) => {
      if (logLevel === LogLevels.ERROR || logLevel === LogLevels.FATAL)
        logRocket.error(msg);

      const critical = isCritical(logLevel);

      if (!critical) {
        if (samplingRate < 1 && Math.random() >= samplingRate) return true;

        if (maxLogsPerInterval > 0 && logCountInWindow >= maxLogsPerInterval)
          return true;

        logCountInWindow += 1;
      }

      init();

      if (critical) {
        await sendLogs(msg);
      } else {
        logs.push(msg);
        if (logs.length >= maxBatchSize) {
          flushBatch();
        }
      }
      return true;
    },
  };
};
